/**
 * Color Tokens — LayananSynexa Cyan Palette
 * ============================================
 * Source of truth for all color values in JS/React.
 * - Use for inline styles, chart configs, dynamic colors
 * - Mirrors the CSS variables in color-tokens.css
 *
 * Usage:
 *   import { cyan, chartColors, tintStyle } from '../styles/color-tokens';
 *   <div style={{ color: cyan[400] }} />
 *   <Bar fill={cyan[500]} />
 *   <div style={tintStyle(cyan[400])}><Icon /></div>
 */

// ─── Cyan Spectrum ──────────────────────────────────────────────
export const cyan = Object.freeze({
  50:  '#e6fafc',
  100: '#b3f2f7',
  200: '#80eaf2',
  300: '#4de2ed',
  400: '#11cdef',   // info / highlight
  500: '#14b8c4',   // primary
  600: '#0d9ea8',
  700: '#0d7a8a',   // primary-dark
  800: '#0a5c68',
  900: '#073845',   // primary-darker
  950: '#0a3845',   // deep bg
});

// ─── RGB variants (for rgba() convenience) ─────────────────────
export const cyanRgb = Object.freeze({
  50:  '230, 250, 252',
  100: '179, 242, 247',
  200: '128, 234, 242',
  300: '77,  226, 237',
  400: '17,  205, 239',
  500: '20,  184, 196',
  600: '13,  158, 168',
  700: '13,  122, 138',
  800: '10,  92,  104',
  900: '7,   56,  69',
  950: '10,  56,  69',
});

// ─── Semantic Colors ───────────────────────────────────────────
export const semantic = Object.freeze({
  success: '#2dce89',
  warning: '#fb6340',
  danger:  '#f5365c',
  info:    cyan[400],
});

// ─── Gradients ─────────────────────────────────────────────────
export const gradients = Object.freeze({
  accent:      `linear-gradient(135deg, ${cyan[500]} 0%, ${cyan[700]} 100%)`,
  accentH:     `linear-gradient(90deg, ${cyan[400]}, ${cyan[700]})`,
  accent3:     `linear-gradient(135deg, ${cyan[500]} 0%, ${cyan[700]} 50%, ${cyan[900]} 100%)`,
  sidebar:     `linear-gradient(180deg, #166072 0%, #0a3845 100%)`,
});

// ─── Chart Color Palette ───────────────────────────────────────
export const chartColors = Object.freeze([
  cyan[400],   // #11cdef — bright cyan
  cyan[500],   // #14b8c4 — medium teal
  cyan[700],   // #0d7a8a — dark teal
  semantic.success,
  semantic.warning,
  semantic.danger,
  cyan[950],   // #0a3845 — very dark teal
  cyan[900],   // #073845 — near black teal
]);

// ─── Classic Theme Colors (Purple-Blue) ────────────────────────
export const classic = Object.freeze({
  primary:       '#5e72e4',
  primaryDark:   '#324cdd',
  primaryDarker: '#172b4d',
  info:          '#11cdef',
  accentTintBg:  '#eff6ff',
  gradients: Object.freeze({
    accent:      'linear-gradient(135deg, #5e72e4 0%, #324cdd 100%)',
    accentH:     'linear-gradient(90deg, #5e72e4, #11cdef)',
    accent3:     'linear-gradient(135deg, #5e72e4 0%, #324cdd 50%, #172b4d 100%)',
    sidebar:     'linear-gradient(180deg, #172b4d 0%, #1e1e2f 100%)',
  }),
});

// ─── Comprehensive Color Map ───────────────────────────────────
export const colors = Object.freeze({
  ...cyan,
  ...semantic,
  default: '#172b4d',
  dark:    '#1e1e2f',
});

// ─── Icon / Background tint helper ─────────────────────────────
// Returns { background: `${hex}18`, color: hex } for stat-icon-shape
export function tintStyle(hex) {
  return { background: hex + '18', color: hex };
}
